import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SurveyResponse, AnalyticsMetrics, Rating } from '@/types';

// Convert rating number to text label
const ratingToLabel = (rating: Rating): string => {
  const labels: Record<Rating, string> = {
    1: 'Very Poor',
    2: 'Poor',
    3: 'Fair',
    4: 'Good',
    5: 'Excellent',
  };
  return labels[rating] || String(rating);
};

// Export to CSV
export const exportToCSV = (responses: SurveyResponse[]): string => {
  if (responses.length === 0) {
    return '';
  }

  const headers = [
    'ID',
    'Created At',
    'Customer Name',
    'Company Name',
    'Product/Service',
    'Engagement Date',
    'Contact Person',
    'Product Quality - Expectations Met',
    'Product Quality - Performance As Expected',
    'Delivery - Timeliness',
    'Delivery - Process Efficient',
    'Communication - Clear/Timely',
    'Communication - Prompt Responses',
    'Staff - Professional/Courteous/Helpful',
    'Staff - Support Adequate/Effective',
    'Price - Value for Money',
    'Overall - Overall Satisfied',
    'Overall - Would Recommend',
    'Likes Most',
    'Areas for Improvement',
    'Would Do Business Again',
    'Consent Agreed',
    'Consent Timestamp',
    'Source',
    'Handled By Staff',
  ];

  const rows = responses.map((r) => [
    r.id,
    r.createdAt,
    r.details.customerName,
    r.details.companyName,
    r.details.productOrService,
    r.details.engagementDate,
    r.details.contactPerson,
    r.ratings.productQuality_expectationsMet,
    r.ratings.productQuality_performanceAsExpected,
    r.ratings.delivery_timeliness,
    r.ratings.delivery_processEfficient,
    r.ratings.communication_clearTimely,
    r.ratings.communication_promptResponses,
    r.ratings.staff_professionalCourteousHelpful,
    r.ratings.staff_supportAdequateEffective,
    r.ratings.price_valueForMoney,
    r.ratings.overall_overallSatisfied,
    r.ratings.overall_wouldRecommend,
    r.open.likesMost,
    r.open.areasForImprovement,
    r.open.wouldDoBusinessAgain,
    r.consent.agreed ? 'Yes' : 'No',
    r.consent.timestamp,
    r.meta.source,
    r.meta.handledByStaff || '',
  ]);

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  return csvContent;
};

// Export to Excel
export const exportToExcel = (responses: SurveyResponse[]): void => {
  const workbook = XLSX.utils.book_new();

  // Main data sheet
  const data = responses.map((r) => ({
    'ID': r.id,
    'Created At': r.createdAt,
    'Customer Name': r.details.customerName,
    'Company Name': r.details.companyName,
    'Product/Service': r.details.productOrService,
    'Engagement Date': r.details.engagementDate,
    'Contact Person': r.details.contactPerson,
    'Product Quality - Expectations Met': ratingToLabel(r.ratings.productQuality_expectationsMet),
    'Product Quality - Performance As Expected': ratingToLabel(r.ratings.productQuality_performanceAsExpected),
    'Delivery - Timeliness': ratingToLabel(r.ratings.delivery_timeliness),
    'Delivery - Process Efficient': ratingToLabel(r.ratings.delivery_processEfficient),
    'Communication - Clear/Timely': ratingToLabel(r.ratings.communication_clearTimely),
    'Communication - Prompt Responses': ratingToLabel(r.ratings.communication_promptResponses),
    'Staff - Professional/Courteous/Helpful': ratingToLabel(r.ratings.staff_professionalCourteousHelpful),
    'Staff - Support Adequate/Effective': ratingToLabel(r.ratings.staff_supportAdequateEffective),
    'Price - Value for Money': ratingToLabel(r.ratings.price_valueForMoney),
    'Overall - Overall Satisfied': ratingToLabel(r.ratings.overall_overallSatisfied),
    'Overall - Would Recommend': ratingToLabel(r.ratings.overall_wouldRecommend),
    'Likes Most': r.open.likesMost,
    'Areas for Improvement': r.open.areasForImprovement,
    'Would Do Business Again': r.open.wouldDoBusinessAgain,
    'Consent Agreed': r.consent.agreed ? 'Yes' : 'No',
    'Consent Timestamp': r.consent.timestamp,
    'Source': r.meta.source,
    'Handled By Staff': r.meta.handledByStaff || '',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Survey Responses');

  XLSX.writeFile(workbook, `survey-responses-${new Date().toISOString().split('T')[0]}.xlsx`);
};

// Export to PDF
export const exportToPDF = (
  responses: SurveyResponse[],
  metrics?: AnalyticsMetrics
): void => {
  const doc = new jsPDF();
  let yPos = 20;

  // Title
  doc.setFontSize(18);
  doc.text('Customer Satisfaction Survey Report', 14, yPos);
  yPos += 10;

  // Metrics summary if provided
  if (metrics) {
    doc.setFontSize(12);
    doc.text(`Total Responses: ${metrics.totalResponses}`, 14, yPos);
    yPos += 7;
    doc.text(`CSAT Percentage: ${metrics.csatPercentage}%`, 14, yPos);
    yPos += 7;
    doc.text(`Recommendation Percentage: ${metrics.recommendationPercentage}%`, 14, yPos);
    yPos += 10;
  }

  // Table data
  const tableData = responses.map((r) => [
    r.details.customerName,
    r.details.companyName,
    r.details.productOrService,
    r.ratings.overall_overallSatisfied.toString(),
    r.ratings.overall_wouldRecommend.toString(),
    r.consent.agreed ? 'Yes' : 'No',
  ]);

  autoTable(doc, {
    head: [['Customer', 'Company', 'Product/Service', 'Overall Satisfied', 'Would Recommend', 'Consent']],
    body: tableData,
    startY: yPos,
    styles: { fontSize: 8 },
  });

  doc.save(`survey-report-${new Date().toISOString().split('T')[0]}.pdf`);
};

// Download CSV as file
export const downloadCSV = (responses: SurveyResponse[], filename?: string): void => {
  const csv = exportToCSV(responses);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename || `survey-responses-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

